import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import ReactECharts from 'echarts-for-react';
import {
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarSquareIcon,
  CircleStackIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ServerStackIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

function MetricCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary-500/10 text-primary-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-400',
    red: 'bg-red-500/10 text-red-400',
    blue: 'bg-blue-500/10 text-blue-400',
    purple: 'bg-purple-500/10 text-purple-400'
  };
  
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-surface-400 text-sm">{title}</p>
          <p className="text-3xl font-bold text-surface-100 mt-1">{value}</p>
          {subtitle && (
            <p className="text-surface-500 text-sm mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
              {trend === 'up' ? (
                <ArrowTrendingUpIcon className="w-4 h-4" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4" />
              )}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function IssuesSummary({ issues }) {
  if (!issues) return null;
  
  const statusCounts = {};
  issues.byStatus?.forEach(s => {
    statusCounts[s._id] = s.count;
  });
  
  const severityCounts = {};
  issues.bySeverity?.forEach(s => {
    severityCounts[s._id] = s.count;
  });
  
  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-surface-100 mb-4">Issue Summary</h3>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-surface-700/50 rounded-lg p-4">
          <p className="text-surface-400 text-sm">Open</p>
          <p className="text-2xl font-bold text-surface-100">{statusCounts.open || 0}</p>
        </div>
        <div className="bg-surface-700/50 rounded-lg p-4">
          <p className="text-surface-400 text-sm">In Progress</p>
          <p className="text-2xl font-bold text-surface-100">{statusCounts.in_progress || 0}</p>
        </div>
        <div className="bg-surface-700/50 rounded-lg p-4">
          <p className="text-surface-400 text-sm">Resolved</p>
          <p className="text-2xl font-bold text-emerald-400">{statusCounts.resolved || 0}</p>
        </div>
        <div className="bg-surface-700/50 rounded-lg p-4">
          <p className="text-surface-400 text-sm">Won't Fix</p>
          <p className="text-2xl font-bold text-surface-500">{statusCounts.wont_fix || 0}</p>
        </div>
      </div>
      
      <h4 className="text-sm font-medium text-surface-300 mb-3">By Severity (Open Issues)</h4>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-red-400 text-sm">Critical</span>
          <span className="badge-danger">{severityCounts.critical || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-amber-400 text-sm">High</span>
          <span className="badge-warning">{severityCounts.high || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-blue-400 text-sm">Medium</span>
          <span className="badge-info">{severityCounts.medium || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-surface-400 text-sm">Low</span>
          <span className="badge-neutral">{severityCounts.low || 0}</span>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    loadMetrics();
  }, []);
  
  const loadMetrics = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getMetrics();
      setMetrics(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };
  
  // Chart options for trends
  const getTrendChartOption = (data, title, color) => ({
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1e293b',
      borderColor: '#334155',
      textStyle: { color: '#f1f5f9' }
    },
    grid: {
      top: 10,
      right: 10,
      bottom: 30,
      left: 40
    },
    xAxis: {
      type: 'category',
      data: data?.map(d => d._id) || [],
      axisLine: { lineStyle: { color: '#475569' } },
      axisLabel: { color: '#94a3b8', fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    series: [{
      type: 'line',
      data: data?.map(d => d.count) || [],
      smooth: true,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: `${color}40` },
            { offset: 1, color: `${color}05` }
          ]
        }
      },
      lineStyle: { color, width: 2 },
      itemStyle: { color }
    }]
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="card bg-red-500/10 border-red-500/20">
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
          <div>
            <p className="text-red-400 font-medium">Error loading metrics</p>
            <p className="text-red-400/70 text-sm">{error}</p>
          </div>
        </div>
        <button onClick={loadMetrics} className="btn-secondary mt-4">
          Retry
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-surface-100">Platform Dashboard</h1>
        <p className="text-surface-400 mt-1">Overview of your Neoshift BI platform</p>
      </div>
      
      {/* Main metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Companies"
          value={metrics?.companies?.total || 0}
          subtitle={`${metrics?.companies?.active || 0} active`}
          icon={BuildingOfficeIcon}
          color="primary"
        />
        <MetricCard
          title="Users"
          value={metrics?.users?.total || 0}
          subtitle={`${metrics?.users?.active || 0} active, ${metrics?.users?.pending || 0} pending`}
          icon={UsersIcon}
          color="emerald"
        />
        <MetricCard
          title="Dashboards"
          value={metrics?.dashboards || 0}
          icon={ChartBarSquareIcon}
          color="blue"
        />
        <MetricCard
          title="Charts"
          value={metrics?.charts || 0}
          icon={CircleStackIcon}
          color="purple"
        />
      </div>
      
      {/* Secondary metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Data Sources"
          value={metrics?.dataSources || 0}
          icon={ServerStackIcon}
          color="amber"
        />
        <MetricCard
          title="Data Imports"
          value={metrics?.imports?.total || 0}
          subtitle={`${metrics?.imports?.completed || 0} completed, ${metrics?.imports?.failed || 0} failed`}
          icon={CloudArrowUpIcon}
          color="blue"
        />
        <MetricCard
          title="Storage Used"
          value={`${metrics?.storage?.totalGB || 0} GB`}
          icon={ServerStackIcon}
          color="purple"
        />
        <MetricCard
          title="AI Tokens (This Month)"
          value={(metrics?.aiTokens?.thisMonth || 0).toLocaleString()}
          icon={SparklesIcon}
          color="primary"
        />
      </div>
      
      {/* Charts and Issues */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Growth Chart */}
        <div className="card lg:col-span-2">
          <h3 className="text-lg font-semibold text-surface-100 mb-4">Company Growth (Last 30 Days)</h3>
          <div className="h-64">
            <ReactECharts
              option={getTrendChartOption(metrics?.trends?.companies, 'Companies', '#0ea5e9')}
              style={{ height: '100%' }}
            />
          </div>
        </div>
        
        {/* Issues Summary */}
        <IssuesSummary issues={metrics?.issues} />
      </div>
      
      {/* User Growth Chart */}
      <div className="card">
        <h3 className="text-lg font-semibold text-surface-100 mb-4">User Growth (Last 30 Days)</h3>
        <div className="h-64">
          <ReactECharts
            option={getTrendChartOption(metrics?.trends?.users, 'Users', '#10b981')}
            style={{ height: '100%' }}
          />
        </div>
      </div>
    </div>
  );
}

